import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

const DEFAULT_TRIP_ID = import.meta.env.VITE_TRIP_DEFAULT || 'juneau-2026';
const TRIP_COOKIE = 'trip_id';

// Loose schema — keeps shape predictable without rejecting future fields
const TripSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    tagline: z.string().optional(),
    dates: z.object({
      start: z.string(),
      end: z.string(),
      timezone: z.string(),
    }),
    destination: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
    lodging: z.object({}).passthrough(),
    players: z.array(z.object({ id: z.string() }).passthrough()),
    spectators: z.array(z.object({ id: z.string() }).passthrough()).optional(),
    theme: z.string().optional(),
  })
  .passthrough();

const TripContext = createContext(null);

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[-.+*]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict`;
}

function resolveTripId() {
  if (typeof window === 'undefined') return DEFAULT_TRIP_ID;
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('trip');
  if (fromUrl) {
    writeCookie(TRIP_COOKIE, fromUrl);
    return fromUrl;
  }
  return readCookie(TRIP_COOKIE) || DEFAULT_TRIP_ID;
}

export function TripProvider({ children }) {
  const [tripId] = useState(resolveTripId);
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/trips/${tripId}/trip.json`, { credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error(`trip.json ${res.status}`);
        return res.json();
      })
      .then((raw) => {
        const parsed = TripSchema.safeParse(raw);
        if (!parsed.success) throw new Error('trip schema mismatch');
        if (!cancelled) setTrip(parsed.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  const value = useMemo(() => ({ tripId, trip, error }), [tripId, trip, error]);

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
}

export function useTrip() {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error('useTrip must be used inside <TripProvider>');
  return ctx;
}
